import type { ComponentType, SVGProps } from 'react';
import {
  BoxCubeIcon,
  CalenderIcon,
  DocsIcon,
  GridIcon,
  GroupIcon,
  ListIcon,
  PieChartIcon,
  PlugInIcon,
} from '../../vendor/tailadmin/icons';
import type { UserRole } from '@app/shared/schemas/users';

export type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * Navigation data model — single source of truth for sidebar entries.
 *
 * Two entry shapes:
 *   - `NavLink`  — top-level link (rendered as a flat item in the sidebar).
 *   - `NavGroup` — collapsible group with child links. Active child auto-
 *     expands the group (see `app-sidebar.tsx`).
 *
 * Czech labels — UI is Czech-only for now. Migrate to data-testid / i18n
 * keys before locales are added (audit note 2026-05-23 #7).
 *
 * Role allow-lists mirror the role scopes in NOTES.md § "Partner access"
 * + role expansion from the 2026-06-01 navigace audit (groups for Reporty,
 * CRM, Výroba, Partneři, Systém, Dokumentace).
 *
 * Adding a new section:
 *   1. Add the entry here.
 *   2. Add the route in `route-tree.tsx`.
 *   3. If group, add stub entries in `app-sidebar.test.tsx` stubPaths.
 *   4. Add the page module in `pages/sections/` or `pages/admin/`.
 */

export type NavRole = UserRole;

export interface NavLink {
  path: string;
  label: string;
  roles: readonly NavRole[];
  /** Icon shown next to the label in the sidebar (vendor TailAdmin SVG component). */
  icon?: NavIcon;
}

export interface NavGroup {
  label: string;
  roles: readonly NavRole[];
  /** Icon shown next to the group header in the sidebar. */
  icon?: NavIcon;
  children: readonly NavLink[];
}

export type NavEntry = NavLink | NavGroup;

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'children' in entry;
}

/**
 * Section list (2026-06-01 nav restructure — PR #89, 2026-06-11 #72193 konsolidace):
 *   - Přehled               — landing, všichni
 *   - Reporty (group)       — sales pipeline, top zákazníci, operations, průchod zakázky
 *                              (admin + decision_maker; partner reports nemá)
 *   - CRM (group)           — Adresář (sjednocený nad app.partners), Nabídky (admin + decision_maker)
 *   - Výroba (group)        — Zakázky, Poptávky k partnerům
 *                              (admin + decision_maker; partner vidí jen Zakázky)
 *   - Systém (group)        — Uživatelé, Synchronizace, Outbox, Cron joby, Logy (admin only)
 *   - Dokumentace (group)   — Uživatelská, Dokumentace dat (admin only)
 *
 * #72193 (2026-06-11) — Filip 10.6. nahlásil zmatek mezi „CRM → Zákazníci"
 * (`/crm/customers`) a „Partneři → Adresář partnerů" (`/partners`). OBIS
 * má jeden adresář `PARTNERI_PARTNERI` (4 520 řádků, žádné role-koštování
 * zákazník/dodavatel — všech 101 BOTH se používá obojí). Sjednoceno do
 * `/partners` (single source of truth s detail page, machines, badge
 * V aplikaci/Z OBIS, edit modal s detach checkboxem). Stará `/crm/customers`
 * URL teď redirect na `/partners` pro bookmark safety.
 *
 * Partner role záměrně vidí Zakázky jako root link (ne pod skupinou Výroba) —
 * je to jediný úkon, který v UI dělá, group s jediným linkem by byl
 * zbytečný klick. Implementace: po filterNavForRole se „Výroba" pro
 * partnera vyfiltruje na 0 children → group se zahodí; samostatný root
 * link na /orders níž v poli zůstane.
 */
export const navEntries: readonly NavEntry[] = [
  {
    path: '/dashboard',
    label: 'Přehled',
    roles: ['admin', 'decision_maker'],
    icon: GridIcon,
  },
  {
    label: 'CRM',
    roles: ['admin', 'decision_maker'],
    icon: CalenderIcon,
    children: [
      {
        // #72505 — CRM přehled (pipeline + komunikace na první pohled).
        path: '/crm/nastenka',
        label: 'Přehled',
        roles: ['admin', 'decision_maker'],
      },
      {
        // #72196 — kanban board poptávek (app.crm_deals).
        path: '/crm/pipeline',
        label: 'Poptávky',
        roles: ['admin', 'decision_maker'],
      },
      {
        path: '/offers',
        label: 'Nabídky',
        roles: ['admin', 'decision_maker'],
      },
      {
        // #72197 — přehled schůzek napříč partnery (kind=meeting).
        path: '/crm/schuzky',
        label: 'Schůzky',
        roles: ['admin', 'decision_maker'],
      },
      {
        // #72193 — „Adresář" (dříve Zákazníci) sjednocen s Adresářem partnerů.
        // OBIS nerozlišuje zákazníky/dodavatele — jeden seznam pro oba scénáře.
        path: '/partners',
        label: 'Adresář',
        roles: ['admin', 'decision_maker'],
      },
    ],
  },
  {
    label: 'Výroba',
    roles: ['admin', 'decision_maker'],
    icon: BoxCubeIcon,
    children: [
      {
        // #72505 — Výroba přehled (zakázky + subdodávky + Gantt časová osa).
        path: '/vyroba/nastenka',
        label: 'Přehled',
        roles: ['admin', 'decision_maker'],
      },
      {
        path: '/orders',
        label: 'Zakázky',
        roles: ['admin', 'decision_maker'],
      },
      {
        path: '/vyroba/poptavky',
        label: 'Poptávky k partnerům',
        roles: ['admin', 'decision_maker'],
      },
    ],
  },
  {
    // #72154 — „Partneři" = samostatná správcovská sekce (vlastní route
    // /partneri). Stejná komponenta jako Adresář, ale s PARTNERS_CONFIG
    // (natvrdo filtr na system:partner, bez role toggles). Vlastní cesta, aby
    // se v sidebaru nepletla s CRM → Adresář (/partners).
    path: '/partneri',
    label: 'Partneři',
    roles: ['admin', 'decision_maker'],
    icon: GroupIcon,
  },
  {
    label: 'Reporty',
    roles: ['admin', 'decision_maker'],
    icon: PieChartIcon,
    children: [
      {
        path: '/reports/sales-pipeline',
        label: 'Sales pipeline',
        roles: ['admin', 'decision_maker'],
      },
      {
        path: '/reports/customer-orders',
        label: 'Zakázky zákazníků',
        roles: ['admin', 'decision_maker'],
      },
      {
        path: '/reports/partner-orders',
        label: 'Objednávky partnerům',
        roles: ['admin', 'decision_maker'],
      },
      {
        path: '/reports/customers',
        label: 'Top zákazníci',
        roles: ['admin', 'decision_maker'],
      },
      {
        path: '/reports/operations',
        label: 'Operations',
        roles: ['admin', 'decision_maker'],
      },
      {
        path: '/reports/order-flow',
        label: 'Průchod zakázky',
        roles: ['admin', 'decision_maker'],
      },
    ],
  },
  {
    // #72193 — skupina „Partneři" zrušená (sjednoceno do CRM → Adresář
    // výše). Položka byla původně admin-only (user account mgmt per partner);
    // role gate se v CRM verzi rozšířil na admin + decision_maker (Filip i
    // jeho lidi adresář potřebují) — partner-specific admin akce (link na
    // user/generate login) řešíme separátně v M5 partner self-service.
    label: 'Systém',
    roles: ['admin'],
    icon: PlugInIcon,
    children: [
      { path: '/admin/users', label: 'Uživatelé', roles: ['admin'] },
      { path: '/admin/roles', label: 'Role a oprávnění', roles: ['admin'] },
      { path: '/admin/sync', label: 'Synchronizace', roles: ['admin'] },
      { path: '/admin/outbox', label: 'Outbox', roles: ['admin'] },
      { path: '/admin/cron', label: 'Cron joby', roles: ['admin'] },
      { path: '/admin/logs', label: 'Logy', roles: ['admin'] },
      { path: '/admin/codebooks', label: 'Číselníky', roles: ['admin'] },
    ],
  },
  {
    label: 'Dokumentace',
    roles: ['admin'],
    icon: DocsIcon,
    children: [
      { path: '/admin/docs', label: 'Uživatelská', roles: ['admin'] },
      { path: '/admin/docs/data', label: 'Dokumentace dat', roles: ['admin'] },
      { path: '/admin/changelog', label: 'Changelog', roles: ['admin'] },
    ],
  },
  // Partner-only root links — viz docstring nad navEntries pro důvod.
  {
    path: '/partner',
    label: 'Přehled',
    roles: ['partner'],
    icon: GridIcon,
  },
  {
    path: '/orders',
    label: 'Zakázky',
    roles: ['partner'],
    icon: ListIcon,
  },
];

/** Filter the nav tree to entries the given role may see. */
export function filterNavForRole(entries: readonly NavEntry[], role: NavRole): NavEntry[] {
  const result: NavEntry[] = [];
  for (const entry of entries) {
    if (!entry.roles.includes(role)) continue;
    if (isNavGroup(entry)) {
      const visibleChildren = entry.children.filter((c) => c.roles.includes(role));
      if (visibleChildren.length === 0) continue;
      result.push({ ...entry, children: visibleChildren });
    } else {
      result.push(entry);
    }
  }
  return result;
}
